import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CircleNotch } from "@phosphor-icons/react";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import type { AnswersMap, TestQuestion } from "@/types/test";

interface FieldError {
  questionId: string;
  reason: string;
}

interface Props {
  token: string;
  questions: TestQuestion[];
  onSubmitted: () => void;
  onExpired: () => void;
  onAlreadySubmitted: () => void;
}

export function TestQuestionsForm({ token, questions, onSubmitted, onExpired, onAlreadySubmitted }: Props) {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState<AnswersMap>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const setText = (id: string, value: string) => {
    setAnswers((p) => ({ ...p, [id]: value }));
    setErrors((p) => ({ ...p, [id]: "" }));
  };
  const setSingle = (id: string, value: string) => {
    setAnswers((p) => ({ ...p, [id]: value }));
    setErrors((p) => ({ ...p, [id]: "" }));
  };
  const toggleMulti = (id: string, value: string, checked: boolean) => {
    setAnswers((p) => {
      const cur = (p[id] as string[] | undefined) ?? [];
      return { ...p, [id]: checked ? [...cur, value] : cur.filter((v) => v !== value) };
    });
    setErrors((p) => ({ ...p, [id]: "" }));
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/test-assignments/by-token/${token}/submit`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ answers }),
      });
      if (res.status === 410) throw new Error("expired");
      if (res.status === 409) throw new Error("already_submitted");
      if (res.status === 400) {
        const body = (await res.json()) as { error: string; details?: FieldError[] };
        if (body.details) {
          const next: Record<string, string> = {};
          for (const d of body.details) next[d.questionId] = d.reason;
          setErrors(next);
        }
        throw new Error("validation");
      }
      await throwIfNotOk(res, t("tests.public.submitError"));
    },
    onSuccess: () => onSubmitted(),
    onError: (err) => {
      if (err instanceof Error) {
        if (err.message === "expired") onExpired();
        else if (err.message === "already_submitted") onAlreadySubmitted();
        else toast.error(err.message || t("tests.public.submitError"));
      }
    },
  });

  const disabled = mutation.isPending;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
      className="mx-auto max-w-xl space-y-5 p-6"
    >
      {questions.map((q) => (
        <fieldset key={q.id} disabled={disabled} className="space-y-2">
          <legend className="font-medium">
            {q.text}
            {q.required && <span className="text-destructive"> *</span>}
          </legend>
          {q.type === "short_text" && (
            <Input
              type="text"
              value={(answers[q.id] as string) ?? ""}
              onChange={(e) => setText(q.id, e.target.value)}
            />
          )}
          {q.type === "long_text" && (
            <Textarea
              value={(answers[q.id] as string) ?? ""}
              onChange={(e) => setText(q.id, e.target.value)}
              rows={4}
            />
          )}
          {(q.type === "single_choice" || q.type === "likert") &&
            "options" in q && (
              <RadioGroup
                value={(answers[q.id] as string) ?? ""}
                onValueChange={(value) => setSingle(q.id, value)}
              >
                {q.options.map((o) => (
                  <Label key={o.value} className="font-normal">
                    <RadioGroupItem value={o.value} />
                    {o.label}
                  </Label>
                ))}
              </RadioGroup>
            )}
          {q.type === "multi_choice" &&
            "options" in q &&
            q.options.map((o) => {
              const cur = (answers[q.id] as string[] | undefined) ?? [];
              return (
                <Label key={o.value} className="font-normal">
                  <Checkbox
                    checked={cur.includes(o.value)}
                    onCheckedChange={(checked) => toggleMulti(q.id, o.value, checked === true)}
                  />
                  {o.label}
                </Label>
              );
            })}
          {errors[q.id] && (
            <p className="text-sm text-destructive">{t(`tests.public.fieldError.${errors[q.id]}`)}</p>
          )}
        </fieldset>
      ))}
      <Button type="submit" disabled={disabled} size="lg">
        {disabled && <CircleNotch className="animate-spin" />}
        {disabled ? t("tests.public.submitting") : t("tests.public.submit")}
      </Button>
    </form>
  );
}
